/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { MediaButtonTypes } from '../../videoplayerui/infobanner/mediabuttontypes';
import { PlaybackState, TrickPlayType } from '../../videoplayerui/infobanner/PlaybackTypes';
import { Channel } from '../../../core/entities/Channel';
import { ServicesClientImpl } from '../../../core/impls/ServicesClientImpl';
import { IServices } from '../../../core/interfaces/IServices';
import {
    hideStillThereOsdAction,
    videoPlayerIsMediaButtonPressedAction,
    videoPlayerSetPlaybackStateAction,
    videoPlayerStartPlaybackAction,
    videoPlayerToggleIsPlayingAction,
} from '../videoplayer.actions';
import { hideSpinner } from '../../spinner/spinner.reducers';
import { showGuideScreen } from '../../screens/screens.reducers';
import {
    hideFullInfo,
    hideStandardInfo,
    hideTrickPlayMenu,
    showTrickPlayMenu,
} from '../../videoplayerui/infobanner/infobanner.reducers';
import {
    handleAdvance,
    handleFastForward,
    handleReplay,
    handleRewind,
    hideNotSupportedText,
    resetPlaybackParamsVideoplayer,
    disableStillThereTimer,
    resetStillThereTimeout,
} from '../videoplayer.reducers';
import { QoEAppLogger } from '../../../core/logging/qoe/QoEAppLogger';
import { onKeyBackSubtitleAndCCLanguageOverlay } from '../overlays/subtitleandcclanguageoverlay/subtitleandcclanguageoverlay.reducers';
import { onKeyBackAudioTrackOverlay } from '../overlays/audiotrackoverlay/audiotrackoverlay.reducers';
import { hideVideoDebugOverlay } from '../videodebugoverlay/videodebugoverlay.reducers';
import { setCurrentActiveIndexAction } from '../../videoplayerui/infobanner/infobanner.actions';
import { VuLogger } from '../../../core/logging/VULogger';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { hideOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { SocuDataModelImpl } from '../../../core/socu/impls';
import { PlaybackContentType } from '../../../core/socu/enums/PlaybackContentType';
import { VideoManager } from '../../../core/videoplayer';
import { DeviceModel } from '../../../core/device';
import { Log } from '../../../core/logging';

const TAG = 'videoplayernavigation';
const services: IServices = new ServicesClientImpl();

export const isPlaybackNotStarted = (): boolean => {
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();

    if (!videoPlayerIns.isPlaybackStarted()) {
        return true;
    }
    return false;
};

export const isPlayPauseRestricted =
    (trickplayType: TrickPlayType) => (dispatch: Function, getState: Function) => {
        let isRestricted = false;
        const { playbackState } = getState().videoPlayer;
        const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();

        if (isPlaybackNotStarted() || NetworkManager.isNetworkDisconnected()) isRestricted = true;
        else if (
            !videoPlayerIns.trickModeRestrictions()?.allowPause &&
            videoPlayerIns.isTrickplayEnable(playbackState)
        ) {
            isRestricted = false;
        } else if (!videoPlayerIns.trickModeRestrictions()?.allowPause) {
            dispatch(
                videoPlayerIsMediaButtonPressedAction({
                    type: TrickPlayType.PAUSE,
                    isMediaButtonPressed: true,
                }),
            );
            isRestricted = true;
        }
        if (!isRestricted) {
            videoPlayerIns.resetVolumeSpeed(playbackState);
            if (trickplayType === TrickPlayType.PAUSE) {
                VideoManager.getInstance().pause(true);
                dispatch(videoPlayerToggleIsPlayingAction(false));
                dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PAUSE));
                VuLogger.logTrickPlayEvent(
                    VuLogger.TrickplayMode.PAUSE,
                    videoPlayerIns.getCurrentTimeInMs(),
                );
                dispatch(disableStillThereTimer());
            } else if (trickplayType === TrickPlayType.PLAY) {
                VideoManager.getInstance().unPause();
                dispatch(videoPlayerToggleIsPlayingAction(true));
                dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PLAY));
                VuLogger.logTrickPlayEvent(
                    VuLogger.TrickplayMode.PLAY,
                    videoPlayerIns.getCurrentTimeInMs(),
                );
                dispatch(resetStillThereTimeout(true));
            }
        }
    };

export const handlePlayPause = (trickplayType: TrickPlayType) => (dispatch: Function) => {
    dispatch(setCurrentActiveIndexAction(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX));
    dispatch(isPlayPauseRestricted(trickplayType));
};

export const setPositionOnBufferThresholdStart = (activeIndex: number) => (dispatch: Function) => {
    dispatch(setCurrentActiveIndexAction(activeIndex));
};

export const handleTrickplay =
    (trickplayType: TrickPlayType, activeIndex: number, isTrickplayTypeEnable?: boolean) =>
    (dispatch: Function) => {
        let isRestricted = false;
        const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
        if (isPlaybackNotStarted() || NetworkManager.isNetworkDisconnected()) isRestricted = true;
        else if (!isTrickplayTypeEnable) {
            dispatch(
                videoPlayerIsMediaButtonPressedAction({
                    type: trickplayType,
                    isMediaButtonPressed: true,
                }),
            );
            isRestricted = true;
        } else if (
            (trickplayType === TrickPlayType.FF || trickplayType === TrickPlayType.ADVANCE) &&
            videoPlayerIns.isBufferThresholdReachedEnd()
        )
            isRestricted = true;
        else if (
            (trickplayType === TrickPlayType.RW || trickplayType === TrickPlayType.REPLAY) &&
            videoPlayerIns.isBufferThresholdReachedStart()
        )
            isRestricted = true;

        if (!isRestricted) {
            dispatch(setPositionOnBufferThresholdStart(activeIndex));
            switch (trickplayType) {
                case TrickPlayType.RW:
                    dispatch(handleRewind());
                    break;
                case TrickPlayType.REPLAY:
                    dispatch(handleReplay());
                    break;
                case TrickPlayType.FF:
                    dispatch(handleFastForward());
                    break;
                case TrickPlayType.ADVANCE:
                    dispatch(handleAdvance());
                    break;
                default:
            }
        }
    };

export const handleLeftRight =
    (trickplayType: TrickPlayType, activeIndex: number, isTrickplayTypeEnable?: boolean) =>
    (dispatch: Function) => {
        dispatch(showTrickPlayMenu());
        let isRestricted = false;
        const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
        if (isPlaybackNotStarted() || NetworkManager.isNetworkDisconnected()) isRestricted = true;
        else if (!isTrickplayTypeEnable) {
            isRestricted = true;
        } else if (
            trickplayType === TrickPlayType.FF &&
            videoPlayerIns.isBufferThresholdReachedEnd()
        )
            isRestricted = true;
        else if (
            trickplayType === TrickPlayType.RW &&
            videoPlayerIns.isBufferThresholdReachedStart()
        )
            isRestricted = true;

        if (!isRestricted) {
            dispatch(setPositionOnBufferThresholdStart(activeIndex));
        }
    };

const onChannelChangeVideoPlayer = (response: Channel) => (dispatch: Function) => {
    dispatch(hideSpinner());
    dispatch(hideActiveOverlays());
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
    videoPlayerIns.trickModeRestrictions(true);
    dispatch(videoPlayerStartPlaybackAction(response));
    const oSocuDataModelImpl = SocuDataModelImpl.getInstance();
    if (oSocuDataModelImpl.getPlaybackContentType() !== PlaybackContentType.LIVE) {
        VideoManager.getInstance().updateLastViewedPosition();
        oSocuDataModelImpl.resetSocuData();
    }
    VideoManager.getInstance().startPlayback({
        channel: response,
        playbackContentType: PlaybackContentType.LIVE,
    });
    dispatch(hideNotSupportedText());
    dispatch(resetPlaybackParamsVideoplayer());
    VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
    QoEAppLogger.logChannelChanged();
    QoEAppLogger.resetProfilePlayingTime();
    QoEAppLogger.stopMediaHealthTimer();
    Log.i(TAG, 'onChannelChangeVideoPlayer');
};

export const onKeyChannelUpVideoPlayer =
    (channelsType: string, noOfKeysPressed: number) => (dispatch: Function, getState: Function) => {
        const { videoPlayer } = getState();
        const { channel } = videoPlayer;

        services
            .getNextChannel(channel.channelNumber, channelsType, noOfKeysPressed)
            .then((response: Channel) => {
                QoEAppLogger.setWatchContentEndReason(
                    QoEAppLogger.PlaybackEndReason.USER_TUNED_TO_NEXT_CHANNEL,
                );
                dispatch(onChannelChangeVideoPlayer(response));
                Diagnostics.debug(TAG, `Next Channel Number: ${response.channelNumber}`);
                Log.d(TAG, `Next Channel Number: ${response.channelNumber}`);
            })
            .catch((error) => {
                Diagnostics.error(TAG, `No Next Channel Available:${error}`);
                Log.e(TAG, `No Next Channel Available:${error}`);
            });
    };

export const onKeyChannelDownVideoPlayer =
    (channelsType: string, noOfKeysPressed: number) => (dispatch: Function, getState: Function) => {
        const { videoPlayer } = getState();
        const { channel } = videoPlayer;

        services
            .getPreviousChannel(channel.channelNumber, channelsType, noOfKeysPressed)
            .then((response: Channel) => {
                dispatch(onChannelChangeVideoPlayer(response));
                QoEAppLogger.setWatchContentEndReason(
                    QoEAppLogger.PlaybackEndReason.USER_TUNED_TO_PREVIOUS_CHANNEL,
                );
                Diagnostics.debug(TAG, `Previous Channel Number: ${response.channelNumber}`);
                Log.d(TAG, `Previous Channel Number: ${response.channelNumber}`);
            })
            .catch((error) => {
                Diagnostics.error(TAG, `No Previous Channel Available: ${error}`);
                Log.e(TAG, `No Previous Channel Available: ${error}`);
            });
    };

export const hideActiveOverlays = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    Diagnostics.info(TAG, 'hideActiveOverlays');
    Log.i(TAG, 'hideActiveOverlays');
    if (isOverlayDialogActive) dispatch(hideOverlayDialog());
};

export const exitVideoPlayer = () => (dispatch: Function, getState: Function) => {
    const { videoPlayer, videoDebugOverlay } = getState();
    const { isVideoDebugOverlayActive } = videoDebugOverlay;
    const { exitPlayerTimer, stillThereTimer } = videoPlayer;
    if (isVideoDebugOverlayActive) {
        dispatch(hideVideoDebugOverlay());
    }
    if (stillThereTimer) clearTimeout(stillThereTimer);
    if (exitPlayerTimer) clearTimeout(exitPlayerTimer);
    dispatch(hideStillThereOsdAction());
    dispatch(hideTrickPlayMenu());
    dispatch(hideFullInfo());
    dispatch(hideActiveOverlays());
    dispatch(showGuideScreen());
};

export const onKeyBackVideoPlayer = () => (dispatch: Function) => {
    dispatch(exitVideoPlayer());
    if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) {
        QoEAppLogger.setWatchContentEndReason(
            QoEAppLogger.PlaybackEndReason.USER_EXITED_FROM_LIVE_TV,
        );
        QoEAppLogger.logPlaybackStopped();
        QoEAppLogger.resetProfilePlayingTime();
        QoEAppLogger.resetProfilePlayingTime();
        VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.ENDED_BY_USER);

        const oSocuDataModelImpl = SocuDataModelImpl.getInstance();
        if (oSocuDataModelImpl.getPlaybackContentType() !== PlaybackContentType.LIVE) {
            VideoManager.getInstance().updateLastViewedPosition();
            oSocuDataModelImpl.resetSocuData();
        }
    }

    Diagnostics.info(TAG, 'onKeyBackVideoPlayer');
    Log.i(TAG, 'onKeyBackVideoPlayer');
};

export const onKeyLeftVideoPlayer = () => (dispatch: Function) => {
    dispatch(
        handleLeftRight(
            TrickPlayType.RW,
            MediaButtonTypes.REWIND_BUTTON_INDEX,
            VideoManager.getInstance().getVideoPlayerModel().trickModeRestrictions()?.allowRewind,
        ),
    );
    Diagnostics.info(TAG, 'onKeyLeftVideoPlayer');
    Log.i(TAG, 'onKeyLeftVideoPlayer');
};

export const onKeyPauseVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyPauseVideoPlayer');
    Log.i(TAG, 'onKeyPauseVideoPlayer');
    dispatch(handlePlayPause(TrickPlayType.PAUSE));
};

export const onKeyPlayVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyPlayVideoPlayer');
    Log.i(TAG, 'onKeyPlayVideoPlayer');
    dispatch(handlePlayPause(TrickPlayType.PLAY));
};

export const onAppResumeVideoPlayer = () => () => {
    Diagnostics.info(TAG, 'onAppResumeVideoPlayer');
    Log.i(TAG, 'onAppResumeVideoPlayer');
};

export const onAppPauseVideoPlayer = () => (dispatch: Function, getState: Function) => {
    Diagnostics.info(TAG, 'onAppPauseVideoPlayer');
    Log.i(TAG, 'onAppPauseVideoPlayer');
    QoEAppLogger.stopMediaHealthTimer();
    QoEAppLogger.logVisibilityBackground();
    VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.VISIBILITY_BEHIND);

    //  If Socu playback is in progress on full screen and the application visibility changes to
    //  'onpause', navigate back to the guide screen.
    if (SocuDataModelImpl.getInstance().isSocu()) {
        dispatch(exitVideoPlayer());
        return;
    }
    const { playbackState } = getState().videoPlayer;
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
    videoPlayerIns.resetVolumeSpeed(playbackState);
    dispatch(disableStillThereTimer());
    dispatch(onKeyBackSubtitleAndCCLanguageOverlay(true));
    dispatch(onKeyBackAudioTrackOverlay(true));
    dispatch(hideStandardInfo());
    dispatch(hideFullInfo());
    dispatch(hideTrickPlayMenu());
};

export const onKeyStopVideoPlayer = () => (dispatch: Function, getState: Function) => {
    Diagnostics.info(TAG, 'onKeyStopVideoPlayer');
    Log.i(TAG, 'onKeyStopVideoPlayer');
    const { playbackState } = getState().videoPlayer;
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
    if (
        !videoPlayerIns.trickModeRestrictions()?.allowPause &&
        videoPlayerIns.isTrickplayEnable(playbackState)
    )
        dispatch(isPlayPauseRestricted(TrickPlayType.PLAY));
    else dispatch(isPlayPauseRestricted(TrickPlayType.PAUSE));
};

export const onKeyFastForwardVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyFastForwardVideoPlayer');
    Log.i(TAG, 'onKeyFastForwardVideoPlayer');
    dispatch(
        handleTrickplay(
            TrickPlayType.FF,
            MediaButtonTypes.FORWARD_BUTTON_INDEX,
            VideoManager.getInstance().getVideoPlayerModel().trickModeRestrictions()
                ?.allowFastForward,
        ),
    );
};

export const onKeyRewindVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyRewindVideoPlayer');
    Log.i(TAG, 'onKeyRewindVideoPlayer');

    dispatch(
        handleTrickplay(
            TrickPlayType.RW,
            MediaButtonTypes.REWIND_BUTTON_INDEX,
            VideoManager.getInstance().getVideoPlayerModel().trickModeRestrictions()?.allowRewind,
        ),
    );
};

export const onKeyReplayVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyReplayVideoPlayer');
    Log.i(TAG, 'onKeyReplayVideoPlayer');
    dispatch(
        handleTrickplay(
            TrickPlayType.REPLAY,
            MediaButtonTypes.REPLAY_BUTTON_INDEX,
            VideoManager.getInstance().getVideoPlayerModel().trickModeRestrictions()?.allowRewind,
        ),
    );
};

export const onKeyAdvanceVideoPlayer = () => (dispatch: Function) => {
    Diagnostics.info(TAG, 'onKeyAdvanceVideoPlayer');
    Log.i(TAG, 'onKeyAdvanceVideoPlayer');
    dispatch(
        handleTrickplay(
            TrickPlayType.ADVANCE,
            MediaButtonTypes.ADVANCE_BUTTON_INDEX,
            VideoManager.getInstance().getVideoPlayerModel().trickModeRestrictions()
                ?.allowFastForward,
        ),
    );
};

export const onKeyRightVideoPlayer = () => (dispatch: Function) => {
    dispatch(
        handleLeftRight(
            TrickPlayType.FF,
            MediaButtonTypes.FORWARD_BUTTON_INDEX,
            VideoManager.getInstance().getVideoPlayerModel().trickModeRestrictions()
                ?.allowFastForward,
        ),
    );
    Diagnostics.info(TAG, 'onKeyRightVideoPlayer');
    Log.i(TAG, 'onKeyRightVideoPlayer');
};
